<template>
  <div class="bg-white py-16 tablet:py-24">
    <div class="grid-container mb-6">
      <div class="col-span-12 laptop:col-span-8 laptop:col-start-3">
        <h2 class="mb-10 text-headline-l">{{ $t('create.step2.heading') }}</h2>

        <form @submit.prevent>
          <section class="mb-8 space-y-4">
            <h3 class="text-headline-m">
              {{ $t('create.step2.job_details') }}
            </h3>

            <FormInputField v-model="title" :validation="$v.title" :label="$t('create.step2.title')" />

            <div class="grid gap-x-3 gap-y-4 tablet:grid-cols-2">
              <FormInputField v-model="applicationUrl" :validation="$v.applicationUrl" :label="$t('create.step2.application_url')" />

              <FormInputField v-model="applicationEmail" :validation="$v.applicationEmail" :label="$t('create.step2.application_email')" />
            </div>

            <p
              v-if="
                ($v.applicationUrl.$error && $v.applicationUrl.required.$invalid) ||
                ($v.applicationEmail.$error && $v.applicationEmail.required.$invalid)
              "
              class="text-red"
            >
              {{ $t('create.validation.fill_one_of_two') }}
            </p>

            <div class="grid gap-x-3 gap-y-4 tablet:grid-cols-2">
              <FormInputField v-model="salaryFrom" :validation="$v.salaryFrom" :label="$t('create.step2.min_salary')" />

              <FormInputField v-model="salaryTo" :validation="$v.salaryTo" :label="$t('create.step2.max_salary')" />
            </div>

            <div>
              <DateInput
                v-model="startAt"
                class="mb-1"
                :label="$t('create.step2.job_start')"
                :error="$v.startAt.$errors.map((error) => error.$message).join(', ')"
              />

              <p class="text-grey-700">
                {{
                  $t('create.step2.start_at_disclaimer', {
                    immediatelyText: $t('job_details.start_at.immediately'),
                  })
                }}
              </p>
            </div>
          </section>

          <section>
            <h5 class="mb-2 text-headline-s">
              {{ $t('create.step2.locations') }}
            </h5>

            <div class="space-y-3">
              <div v-for="(location, index) in locations" :key="index" class="flex items-center gap-x-2">
                <GoogleCityInput :name="location?.name" :label="$t('create.step2.location')" @update="updateLocation(index, $event)" />

                <button v-if="index > 0" class="btn-secondary-neutral-s" @click="removeLocation(index)">
                  {{ $t('create.step2.remove_location') }}
                </button>
              </div>
            </div>

            <p v-if="$v.locations.$error && $v.locations.$invalid" class="mt-1 text-red">
              {{ $t('create.validation.locations') }}
            </p>

            <button class="btn-secondary-neutral-s mt-3" @click="addLocation">
              {{ $t('create.step2.add_location') }}
            </button>
          </section>

          <FormMultiSelectField
            v-model="locationOptions"
            :options="locationOptionsPossibleValues"
            name="location_options"
            :validation="$v.locationOptions"
          />

          <FormMultiSelectField v-model="jobTypes" :options="jobTypesPossibleValues" name="job_types" :validation="$v.jobTypes" />

          <FormMultiSelectField v-model="experience" :options="experiencePossibleValues" name="experience" :validation="$v.experience" />

          <DataFetcher :is-loading="jobCategoriesLoading">
            <FormMultiSelectField
              v-model="jobCategories"
              :options="orderedJobCategories"
              name="categories"
              :validation="$v.jobCategories"
            />
          </DataFetcher>

          <section class="mb-8 space-y-4">
            <h3 class="text-headline-m">
              {{ $t('create.step2.company_details') }}
            </h3>

            <FormInputField v-model="companyName" :validation="$v.companyName" :label="$t('create.step2.company_name')" />

            <h5 class="text-headline-s">
              {{ `${$t('create.step2.company_logo')}*` }}
            </h5>

            <div
              class="relative space-y-3 border border-dashed border-mint pt-5 pb-10 text-center transition-colors duration-200 ease-in-out hover:bg-black/10"
            >
              <img v-if="companyLogoThumb" :src="companyLogoThumb" class="mx-auto my-0" />

              <img v-else src="~/assets/images/upload.svg" class="mx-auto my-0" />

              <h5 class="text-headline-s">
                {{ $t('create.step2.drag_and_drop_or') }}
              </h5>

              <p v-if="companyLogo" class="btn-secondary-neutral-s">
                {{ companyLogo.name }}
              </p>

              <button v-else class="btn-secondary-neutral-s">
                {{ $t('create.step2.select') }}
              </button>

              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                class="absolute inset-x-0 inset-y-0 cursor-pointer opacity-0"
                @change="handleLogoChange"
              />

              <p>
                {{ $t('create.step2.logo_hint_small') }}
              </p>
            </div>

            <ValidationError :validation="$v.companyLogo" />
          </section>

          <section class="space-y-4">
            <h5 class="text-headline-s">
              {{ $t('create.step2.company_address') }}
            </h5>

            <CountrySelect v-model="country" top-country="DE" :validation="$v.country" :placeholder="$t('create.step2.country')" />

            <FormInputField v-model="city" :label="$t('create.step2.city')" :validation="$v.city" />

            <FormInputField v-model="zipcode" :label="$t('create.step2.zipcode')" :validation="$v.zipcode" />

            <FormInputField v-model="street" :label="$t('create.step2.street')" :validation="$v.street" />
          </section>
        </form>
      </div>
    </div>

    <JobWizardFooter
      :job-offer-price="jobOfferPrice || undefined"
      :show-price="isNewJob"
      :step-count="stepCount"
      :current-step-count="currentStepCount"
      :validator="$v"
      @next-step="$emit('nextStep')"
      @previous-step="$emit('previousStep')"
    />
  </div>
</template>

<script setup lang="ts">
import { DateInput } from '@ramp106/omrjs-core-ui/deprecated'
import { useVuelidate, type ValidatorFn } from '@vuelidate/core'
import { email, numeric, requiredIf, url, required as requiredWithoutI18n, helpers } from '@vuelidate/validators'
import { onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DataFetcher from '@/components/common/DataFetcher.vue'
import CountrySelect from '@/components/CountrySelect.vue'
import GoogleCityInput from '@/components/CreateJob/GoogleCityInput.vue'
import JobWizardFooter from '@/components/CreateJob/JobWizardFooter.vue'
import ValidationError from '@/components/CreateJob/ValidationError.vue'
import FormInputField from '@/components/Form/FormInputField.vue'
import FormMultiSelectField from '@/components/Form/FormMultiSelectField.vue'
import type { Location } from '@/gql/jobs/schema.generated.js'
import { locationOptionsPossibleValues, jobTypesPossibleValues, experiencePossibleValues } from '@/helpers/constants'
import i18nValidator from '@/helpers/i18nValidator'
import { orderedTranslatedJobCategories } from '@/helpers/jobHelpers'

import { useJobCategories } from '~/composables/useJobCategories'
import { useNewJobStore } from '~/store/newJob'

interface JobWizardStepDetailsProps {
  isNewJob?: boolean
  stepCount: number
  currentStepCount: number
}
const props = withDefaults(defineProps<JobWizardStepDetailsProps>(), {
  isNewJob: false,
})

defineEmits(['nextStep', 'previousStep'])

const { t } = useI18n()
const store = useNewJobStore()
const { jobCategoriesLoading, jobCategories: allJobCategories, fetchJobCategories } = useJobCategories()

const required = i18nValidator(requiredWithoutI18n)

const orderedJobCategories = computed(() => {
  return orderedTranslatedJobCategories(allJobCategories.value)
})

onMounted(() => {
  fetchJobCategories()
})

const handleLogoChange = (event: Event) => {
  const input = event.target as HTMLInputElement | null
  const file = input?.files?.[0]

  if (file) companyLogo.value = file
}

const companyLogoThumb = computed(() => {
  return store.companyLogoThumb
})

const jobOfferPrice = computed(() => {
  return store.jobOfferPrice
})

const companyLogo = computed({
  get() {
    return store.jobOffer.companyLogo
  },
  set(newValue) {
    store.updateJobOffer({ companyLogo: newValue })
  },
})

const title = computed({
  get() {
    return store.jobOffer.title
  },
  set(newValue) {
    store.updateJobOffer({ title: newValue })
  },
})

const startAt = computed({
  get() {
    return store.jobOffer.startAt
  },
  set(newStartAt) {
    store.updateJobOffer({
      startAt: newStartAt,
    })
  },
})

const applicationUrl = computed({
  get() {
    return store.jobOffer.applicationUrl || ''
  },
  set(newValue) {
    store.updateJobOffer({ applicationUrl: newValue })
  },
})

const applicationEmail = computed({
  get() {
    return store.jobOffer.applicationEmail
  },
  set(newValue) {
    store.updateJobOffer({ applicationEmail: newValue })
  },
})

const companyName = computed({
  get() {
    return store.jobOffer.companyName
  },
  set(newValue) {
    store.updateJobOffer({ companyName: newValue })
  },
})

const locations = computed({
  get() {
    return store.locations
  },
  set(newValue) {
    store.updateLocations(newValue)
  },
})

const addLocation = () => {
  locations.value = [...locations.value, {}]
}

const removeLocation = (index: number) => {
  const newLocations = [...locations.value]
  newLocations.splice(index, 1)
  locations.value = newLocations
}

const updateLocation = (index: number, value: Location | Record<string, never>) => {
  const newLocations = [...locations.value]
  newLocations[index] = value
  locations.value = newLocations
}

const jobTypes = computed({
  get() {
    return store.jobOffer.jobTypes || []
  },
  set(newValue) {
    store.updateJobOffer({ jobTypes: newValue })
  },
})

const locationOptions = computed({
  get() {
    return store.jobOffer.locationOptions || []
  },
  set(newValue) {
    store.updateJobOffer({ locationOptions: newValue })
  },
})

const experience = computed({
  get() {
    return store.jobOffer.experience || []
  },
  set(newValue) {
    store.updateJobOffer({ experience: newValue })
  },
})

const jobCategories = computed({
  get() {
    return store.jobOffer.categoryIds || []
  },
  set(newValue) {
    store.updateJobOffer({ categoryIds: newValue })
  },
})

const country = computed({
  get() {
    return store.jobOffer.country
  },
  set(newValue) {
    store.updateJobOffer({ country: newValue })
  },
})

const city = computed({
  get() {
    return store.jobOffer.city
  },
  set(newValue) {
    store.updateJobOffer({ city: newValue })
  },
})

const zipcode = computed({
  get() {
    return store.jobOffer.zipcode
  },
  set(newValue) {
    store.updateJobOffer({ zipcode: newValue })
  },
})

const street = computed({
  get() {
    return store.jobOffer.street
  },
  set(newValue) {
    store.updateJobOffer({ street: newValue })
  },
})

const salaryFrom = computed({
  get() {
    return store.jobOffer.salaryFrom
  },
  set(newValue) {
    store.updateJobOffer({ salaryFrom: newValue })
  },
})

const salaryTo = computed({
  get() {
    return store.jobOffer.salaryTo
  },
  set(newValue) {
    store.updateJobOffer({ salaryTo: newValue })
  },
})

const uriValidator: ValidatorFn = (value: string, siblingState, vm) => {
  return (url.$validator(value, siblingState, vm) || url.$validator('http://' + value, siblingState, vm)) && !value.includes('@')
}
const locationsValidator = (value: Partial<Location>[]) => {
  if (locationOptions.value.length === 1 && locationOptions.value[0] === 'remote') {
    return true
  } else {
    return Boolean(value?.[0]?.name)
  }
}

const isLogoRequired = computed(() => {
  return props.isNewJob
})

const rules = {
  title: { required },
  applicationUrl: {
    uriValidator: helpers.withMessage(t('create.validation.invalid_url'), uriValidator),
    required: requiredIf(() => !applicationEmail.value),
  },
  applicationEmail: {
    email,
    required: requiredIf(() => !applicationUrl.value),
  },
  companyName: { required },
  city: { required },
  startAt: { required },
  jobTypes: { required },
  locationOptions: { required },
  locations: { locationsValidator },
  experience: { required },
  jobCategories: { required },
  companyLogo: {
    required: helpers.withMessage(
      t('create.validation.logo_required'),
      requiredIf(() => isLogoRequired.value),
    ),
  },
  country: { required },
  street: { required },
  zipcode: { required },
  salaryFrom: { numeric },
  salaryTo: { numeric },
}

const $v = useVuelidate(rules, {
  title,
  applicationUrl,
  applicationEmail,
  companyName,
  city,
  startAt,
  jobTypes,
  locationOptions,
  locations,
  experience,
  jobCategories,
  companyLogo,
  country,
  street,
  zipcode,
  salaryFrom,
  salaryTo,
})
</script>
